import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Tooltip } from 'antd';
import clsx from 'clsx';

import {
	actionEmployeesDeleteClaims,
	actionEmployeesSetClaims,
} from '../../redux/action/accessRights/actionUsersList';

import { reducerGetUsersClaims } from '../../redux/reducer/accessRights/reducers/usersList';

import { reducerRolesRights } from '../../redux/reducer/accessRights/reducers/roles';

import { actionGetRolesRights } from '../../redux/action/accessRights/actionRoles';

import { reducerUsersClaims } from '../../redux/reducer/accessRights/reducers/usersList';

import { AppDispatch } from '../../redux/reducer/store';

import Blackout from '../custom/blackout/blackout';
import EditClaimDescriptionModal from '../editClaimDescriptionModal/editClaimDescriptionModal';

import { ReactComponent as DirectionIcon } from '../../assets/icons/direction.svg';
import { ReactComponent as QuestionIcon } from '../../assets/icons/question.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import Close from '../../assets/icons/close.svg';

import styles from './rolesModal.module.scss';
import { IUserOptions } from '../../types/interfaces';

interface IModalOptions {
	title: string;
	onClose: () => void;
	id: number;
	role: string;
};

interface IClaimOptions {
	description: string;
	role: { id: number; name: string };
};

const RolesEmployeesModal: FC<IModalOptions> = ({ onClose, title, id, role }) => {
	const dispatch = useDispatch<AppDispatch>();
	const { activeClaimsList, rolesList, claimsList } = useSelector((state: any) => state.roles.usersList);
	const { roleRights, allRolesRights } = useSelector(
		(state: any) => state.roles.roles
	);
	const { claims } = useSelector((state: IUserOptions) => state.user.user.data);

	const [selectedUsedRoles, setSelectedUsedRoles] = useState<number[]>([]);
	const [selectedAllRoles, setSelectedAllRoles] = useState<number[]>([]);
	const [usedRolesSearch, setUsedRolesSearch] = useState<string>('');
	const [allRolesSearch, setAllRolesSearch] = useState<string>('');
	const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
	const [currentElem, setCurrentElem] = useState<IClaimOptions | null>(null);
	const [allClaimsList, setAllClaimsList] = useState(allRolesRights);

	const filteredAllRoles =
		allClaimsList &&
		allClaimsList?.filter((option: IClaimOptions) =>
			option?.role.name?.toLowerCase()?.includes(allRolesSearch?.toLowerCase())
		);

	const allRights = (!claimsList || claimsList.length === 0) ? filteredAllRoles : filteredAllRoles?.filter((option: IClaimOptions) =>
		claimsList && claimsList.every((claim: { id: number, name: string }) => claim.id !== option?.role.id)
	);

	const availableRights = (!claimsList || claimsList.length === 0) ? [] : allClaimsList?.filter((option: IClaimOptions) =>
		claimsList.some((claim: { id: number, name: string }) => claim.id === option?.role.id)
	)?.map((item: IClaimOptions) => ({ id: item.role.id, name: item.role.name, description: item.description }));

	const foundRole = rolesList ? rolesList.find((el: { claimsCount: number, role: { id: number, name: string } }) =>
		el.role.name === role) : [];

	const combinedRights = () => {
		if (!activeClaimsList && !claimsList) return [];

		const repeatingElement = (claimsList && activeClaimsList) && activeClaimsList.filter((option: IClaimOptions) =>
			claimsList.some((claim: { id: number, name?: string }) => claim.id === option?.role.id));

		const filteredActiveClaimsList = repeatingElement ? activeClaimsList.filter((option: IClaimOptions) =>
			!repeatingElement.some((repeating: IClaimOptions) => option.role.id === repeating.role.id)
		) : activeClaimsList;

		const combinedRights = (filteredActiveClaimsList && availableRights) ? filteredActiveClaimsList.concat(availableRights) : (filteredActiveClaimsList || availableRights);

		repeatingElement && repeatingElement.forEach((el: IClaimOptions) => combinedRights.push({ id: el.role.id, name: el.role.name, description: el.description }));

		return combinedRights;
	};

	const filteredUsedRoles = combinedRights()
		? combinedRights().filter((option: any) => {
			return (
				option?.role ? option?.role?.name?.toLowerCase()?.includes(usedRolesSearch?.toLowerCase()) : option?.name?.toLowerCase()?.includes(usedRolesSearch?.toLowerCase())
			);
		})
		: [];

	useEffect(() => {
		if (rolesList) {
			dispatch(actionGetRolesRights(foundRole.role.id));
		}
		// eslint-disable-next-line
	}, [rolesList]);

	useEffect(() => {
		dispatch(reducerUsersClaims(roleRights));
		setAllClaimsList(allRolesRights);
		// eslint-disable-next-line
	}, [roleRights]);

	const handleAllRoleClick = (roleId: number) => {
		if (selectedAllRoles.includes(roleId)) {
			setSelectedAllRoles(selectedAllRoles.filter(id => id !== roleId));
		} else {
			setSelectedAllRoles([...selectedAllRoles, roleId]);
		}
	};

	const handleUsedRoleClick = (roleId: number) => {
		if (selectedUsedRoles.includes(roleId)) {
			setSelectedUsedRoles(selectedUsedRoles.filter(id => id !== roleId));
		} else {
			setSelectedUsedRoles([...selectedUsedRoles, roleId]);
		}
	};

	const handleAddSelectedRoles = () => {
		if (selectedAllRoles.length > 0) {
			const filteredRoles = allClaimsList
				.filter((roleObj: { role: { id: number, name: string } }) => selectedAllRoles.includes(roleObj.role.id))
				.map((elem: { role: { id: number, name: string } }) => elem.role);
			const allClaims = claimsList ? filteredRoles.concat(claimsList) : filteredRoles;
			dispatch(actionEmployeesSetClaims(id, filteredRoles)).then(() => {
				dispatch(reducerGetUsersClaims(allClaims));
			});
			setSelectedAllRoles([]);
		}
	};

	const handleDeleteSelectedRoles = () => {
		if (selectedUsedRoles.length > 0) {
			const filteredRoles = selectedUsedRoles.map((elem: number) => ({ id: elem, name: '' }));
			dispatch(actionEmployeesDeleteClaims(id, filteredRoles)).then(() => {
				const allClaims = combinedRights().filter((right: { id: number, name: string, description: string }) => {
					return !filteredRoles.some((role: { id: number, name: string }) => role.id === right.id);
				});
				dispatch(reducerGetUsersClaims(allClaims));
			});
			setSelectedUsedRoles([]);
		}
	};

	const handleClickTooltip = (event: React.MouseEvent) => {
		event.stopPropagation();
	};

	const handleEditClaimDescription = (elem: IClaimOptions) => {
		setCurrentElem(elem);
		setIsEditOpen(!isEditOpen);
	};

	const handleClose = () => {
		dispatch(reducerUsersClaims([]));
		dispatch(reducerRolesRights({ availableClaims: null, activeClaims: null }))
		onClose();
	};

	return (
		<>
			<Blackout onClose={handleClose}>
				<div
					onClick={(event: React.MouseEvent) => event.stopPropagation()}
					className={styles.modal}
				>
					<div className={styles.title__block}>
						<span>{title}</span>
						<img
							style={{ cursor: 'pointer' }}
							onClick={onClose}
							src={Close}
							alt='close'
						/>
					</div>
					<div className={styles.roles__block}>
						<div className={styles.button__block}>
							<div className={styles.roles__table}>
								<div className={styles.roles__title_block}>
									<span>Доступные права</span>
								</div>
								<input
									onChange={e => setAllRolesSearch(e?.target?.value)}
									className={styles.roles__search}
									placeholder='Поиск...'
									type='text'
								/>
								<div className={styles.roles__list}>
									{filteredAllRoles &&
										allRights.map((elem: IClaimOptions, index: number) => (
											<div
												onClick={() => handleAllRoleClick(elem.role.id)}
												className={clsx(
													styles.role__elem,
													selectedAllRoles.includes(elem.role.id) &&
													styles.active__role
												)}
												key={index}
											>
												<span>{elem.role.name}</span>
												<Tooltip
													title={
														<div
															className={styles.tooltip__block}
															onClick={handleClickTooltip}
														>
															<div
																className={styles.tooltip__text}
																dangerouslySetInnerHTML={{
																	__html: elem.description,
																}}
															/>
															{(claims.includes('admin') || claims.includes('claim:claims:edit')) && <EditIcon
																onClick={() => handleEditClaimDescription(elem)}
																className={styles.edit__icon}
															/>}
														</div>
													}
													color={'var(--main-color)'}
													placement='right'
												>
													<QuestionIcon
														className={clsx(
															styles.role__elem_icon,
															selectedAllRoles.includes(elem.role.id) &&
															styles.active__role_icon
														)}
													/>
												</Tooltip>
											</div>
										))}
								</div>
							</div>
							{(claims.includes('admin') || claims.includes('claim:claims:edit')) && <button
								className={clsx(
									styles.add__table_disabledButton,
									selectedAllRoles.length > 0 &&
									(claims.includes('admin') ||
										claims.includes('claim:claims:edit')) &&
									styles.add__table_activeButton
								)}
								onClick={handleAddSelectedRoles}
							>
								Добавить выбранные
								<DirectionIcon className={styles.add__direction_icon} />
							</button>}
						</div>

						<div className={styles.button__block}>
							<div className={styles.roles__table}>
								<div className={styles.roles__title_block}>
									<span>Назначенные права</span>
								</div>
								<input
									onChange={e => setUsedRolesSearch(e?.target?.value)}
									className={styles.roles__search}
									placeholder='Поиск...'
									type='text'
								/>
								<div className={styles.roles__list}>
									{filteredUsedRoles &&
										filteredUsedRoles.map((elem: any, index: number) => (
											<div
												onClick={() => (!elem.role && handleUsedRoleClick(elem.id))}
												className={clsx(
													elem.role ? styles.role__elem_disabled : styles.role__elem,
													selectedUsedRoles.includes(elem.role ? elem.role.id : elem.id) &&
													styles.active__role
												)}
												key={index}
											>
												<span>{elem.role ? elem.role.name : elem.name}</span>
												<Tooltip
													title={
														<div
															className={styles.tooltip__block}
															onClick={handleClickTooltip}
														>
															<div
																className={styles.tooltip__text}
																dangerouslySetInnerHTML={{
																	__html: elem.description,
																}}
															/>
															{(claims.includes('admin') || claims.includes('claim:claims:edit')) && <EditIcon
																onClick={() => handleEditClaimDescription(elem)}
																className={styles.edit__icon}
															/>}
														</div>
													}
													color={'var(--main-color)'}
													placement='right'
												>
													<QuestionIcon
														className={clsx(
															styles.role__elem_icon,
															selectedUsedRoles.includes(elem.id) &&
															styles.active__role_icon
														)}
													/>
												</Tooltip>
											</div>
										))}
									{filteredUsedRoles.length === 0 && (
										<span className={styles.empty__list_text}>
											Нет назначенных прав
										</span>
									)}
								</div>
							</div>
							{(claims.includes('admin') || claims.includes('claim:claims:edit'))
								&& <button
									className={clsx(
										styles.delete__table_disabledButton,
										selectedUsedRoles.length > 0 &&
										(claims.includes('admin') ||
											claims.includes('claim:claims:edit')) &&
										styles.delete__table_activeButton
									)}
									onClick={handleDeleteSelectedRoles}
								>
									<DirectionIcon className={styles.delete__direction_icon} />
									Удалить выбранные
								</button>}
						</div>
					</div>
				</div>
			</Blackout>
			{(isEditOpen && currentElem) && (
				<EditClaimDescriptionModal
					setState={setIsEditOpen}
					state={isEditOpen}
					id={foundRole.role.id}
					elem={currentElem}
				/>
			)}
		</>
	);
};

export default RolesEmployeesModal;
